import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../../../constants/api';
import { getToken } from '../../../utils/TokenUtils';
import '../css/InsertTab.css';

export default function InsertTab({ table }) {
  const [columns, setColumns] = useState([]);
  const [formData, setFormData] = useState({});
  const [functions, setFunctions] = useState({});
  const [nullFlags, setNullFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const token = getToken();

  if (token) {
    try {
      // Standard JWT layout: [Header].[Payload].[Signature]
      const payloadBase64 = token.split('.')[1];

      // Handle URL-safe base64 string replacements
      const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');

      // Decode base64 and parse JSON
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const decoded = JSON.parse(jsonPayload);

      console.log('Decoded JWT Payload:', decoded);

      // Optional: Log token expiration info if 'exp' claim is present
      if (decoded.exp) {
        const expDate = new Date(decoded.exp * 1000);
        const isExpired = Date.now() >= decoded.exp * 1000;
        console.log(
          `Expires: ${expDate.toLocaleString()} (${isExpired ? 'EXPIRED' : 'VALID'})`
        );
      }
    } catch (error) {
      console.error('Invalid JWT format or decode error:', error);
    }
  } else {
    console.warn('No token retrieved from getToken()');
  }
  //   console.log('token:', token);

  // Common SQL functions supported in phpMyAdmin
  const sqlFunctions = [
    '',
    'NOW',
    'CURDATE',
    'CURTIME',
    'MD5',
    'SHA1',
    'PASSWORD',
    'RAND',
    'LOWER',
    'UPPER',
  ];

  useEffect(() => {
    const fetchStructure = async () => {
      setLoading(true);
      setMessage(null);
      const url = `${API_URL}/${ENDPOINTS.SQL}/structure/${table}`;

      //   console.log('url:', url);
      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        });

        // console.log('response:', response);

        const cols = response.data || [];
        setColumns(cols);

        // Initialize form state
        const initialForm = {};
        const initialNulls = {};
        const initialFuncs = {};

        cols.forEach((col) => {
          initialForm[col.Field] =
            col.Default !== null && col.Default !== undefined
              ? col.Default
              : '';
          initialNulls[col.Field] = col.Null === 'YES' && col.Default === null;
          initialFuncs[col.Field] = '';
        });

        setFormData(initialForm);
        setNullFlags(initialNulls);
        setFunctions(initialFuncs);
      } catch (error) {
        console.error('Error fetching structure:', error);
      } finally {
        setLoading(false);
      }
    };

    if (table) fetchStructure();
  }, [table, token]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFunctionChange = (field, func) => {
    setFunctions((prev) => ({ ...prev, [field]: func }));
  };

  const handleNullToggle = (field, checked) => {
    setNullFlags((prev) => ({ ...prev, [field]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Build the payload considering NULL checkboxes
    const payload = {};
    columns.forEach((col) => {
      const field = col.Field;
      if (nullFlags[field]) {
        payload[field] = null;
      } else {
        payload[field] = formData[field];
      }
    });

    try {
      await axios.post(
        `${API_URL}/${ENDPOINTS.SQL}/insert/${table}`,
        { data: payload, functions },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );
      setMessage({ type: 'success', text: '1 row inserted.' });
    } catch (error) {
      console.error('Error inserting row:', error);
      setMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Failed to insert row.',
      });
    }
  };

  const handleReset = () => {
    const resetForm = {};
    const resetNulls = {};
    const resetFuncs = {};

    columns.forEach((col) => {
      resetForm[col.Field] = '';
      resetNulls[col.Field] = false;
      resetFuncs[col.Field] = '';
    });

    setFormData(resetForm);
    setNullFlags(resetNulls);
    setFunctions(resetFuncs);
    setMessage(null);
  };

  if (loading) {
    return <div className="pma-loading">Loading column structure...</div>;
  }

  if (!columns || !columns.length) {
    return (
      <div className="pma-alert pma-alert-danger">
        Unable to load structure for table <strong>{table}</strong>.
      </div>
    );
  }

  return (
    <div className="pma-insert-wrapper">
      {message && (
        <div className={`pma-alert pma-alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="pma-insert-box">
          <div className="pma-insert-legend">
            Insert into table <span className="pma-tablename">`{table}`</span>
          </div>

          <div className="table-responsive">
            <table className="pma-insert-table">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>Column</th>
                  <th style={{ width: '20%' }}>Type</th>
                  <th style={{ width: '15%' }}>Function</th>
                  <th style={{ width: '8%', textAlign: 'center' }}>Null</th>
                  <th style={{ width: '37%' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {columns.map((col) => {
                  const isAutoIncrement = col.Extra?.includes('auto_increment');
                  const isNullChecked = nullFlags[col.Field] || false;

                  return (
                    <tr key={col.Field}>
                      <td className="pma-col-name">{col.Field}</td>
                      <td className="pma-col-type">{col.Type}</td>
                      <td>
                        <select
                          className="pma-select"
                          value={functions[col.Field] || ''}
                          disabled={isNullChecked}
                          onChange={(e) =>
                            handleFunctionChange(col.Field, e.target.value)
                          }
                        >
                          {sqlFunctions.map((fn) => (
                            <option key={fn} value={fn}>
                              {fn}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {col.Null === 'YES' ? (
                          <input
                            type="checkbox"
                            checked={isNullChecked}
                            onChange={(e) =>
                              handleNullToggle(col.Field, e.target.checked)
                            }
                          />
                        ) : (
                          <span className="pma-disabled-text">-</span>
                        )}
                      </td>
                      <td>
                        {col.Type.includes('text') ? (
                          <textarea
                            className="pma-input pma-textarea"
                            rows={2}
                            disabled={isNullChecked}
                            value={formData[col.Field] || ''}
                            onChange={(e) =>
                              handleInputChange(col.Field, e.target.value)
                            }
                            placeholder={
                              isAutoIncrement ? 'auto_increment' : ''
                            }
                          />
                        ) : (
                          <input
                            type={
                              col.Type.includes('int')
                                ? 'number'
                                : col.Type.includes('date')
                                  ? 'date'
                                  : 'text'
                            }
                            className="pma-input"
                            disabled={isNullChecked}
                            value={formData[col.Field] || ''}
                            onChange={(e) =>
                              handleInputChange(col.Field, e.target.value)
                            }
                            placeholder={
                              isAutoIncrement ? 'auto_increment' : ''
                            }
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pma-insert-toolbar">
            <button
              type="button"
              className="pma-btn pma-btn-secondary"
              onClick={handleReset}
            >
              Reset
            </button>
            <button type="submit" className="pma-btn pma-btn-primary">
              Go
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
